// /app/frontend/src/pages/CustomerPortalPage.jsx
// Customer Portal - View appointments, invoices, and request reschedules

import React, { useState, useEffect, useCallback } from 'react';
import { PortalInvoiceCard } from '../components/portal/PortalInvoiceCard';
import { PortalSiteLink } from '../components/portal/PortalSiteLink';
import { useBrandName } from '../hooks/useCompanyBrand';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, Clock, MapPin, User, FileText, Loader2, LogOut,
  Mail, Send, CheckCircle, XCircle, AlertCircle, RefreshCw, Home,
  ShoppingBag, Package, Wrench, Bell, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { PortalDeposit } from '../components/payments/DepositControls';

const API = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (v) => v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

export default function CustomerPortalPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const brandName = useBrandName();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [verifyingToken, setVerifyingToken] = useState(false);
  
  // Login state
  const [email, setEmail] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  
  // Data state
  const [appointments, setAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Reschedule modal
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleAppointment, setRescheduleAppointment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [submittingReschedule, setSubmittingReschedule] = useState(false);

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState({ order_status: true, installation_date: true, promotions: false });
  const [savingPrefs, setSavingPrefs] = useState(false);

  const headers = sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};

  // Quote the customer came for (from an emailed View Quote link); ownership is enforced by the portal's own quote list
  const [focusQuote] = useState(() => searchParams.get('quote'));
  const [linkProblem, setLinkProblem] = useState('');
  const [focusInvoice] = useState(() => searchParams.get('invoice'));
  const [activeTab, setActiveTab] = useState(focusInvoice ? 'invoices' : focusQuote ? 'quotes' : 'appointments');
  const portalPath = focusInvoice ? `/customer-portal?invoice=${encodeURIComponent(focusInvoice)}`
    : focusQuote ? `/customer-portal?quote=${encodeURIComponent(focusQuote)}` : '/customer-portal';

  const restoreStoredSession = () => {
    const storedToken = localStorage.getItem('customer_session_token');
    const storedCustomer = localStorage.getItem('customer_data');
    if (!storedToken || !storedCustomer) return false;
    setSessionToken(storedToken);
    setCustomer(JSON.parse(storedCustomer));
    setIsAuthenticated(true);
    return true;
  };

  // Check for magic link token on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicToken(token);
    } else {
      restoreStoredSession();
    }
  }, [searchParams]);

  // Fetch data when authenticated
  useEffect(() => {
    if (focusQuote && activeTab === 'quotes') {
      document.querySelector('[data-testid="portal-focused-quote"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [quotes]);

  useEffect(() => {
    if (isAuthenticated && sessionToken) {
      fetchAppointments();
      fetchInvoices();
      fetchQuotes();
      fetchOrders();
      fetchNotifPrefs();
      const sid = searchParams.get('session_id');
      if (searchParams.get('deposit') === 'success' && sid) {
        axios.get(`${API}/api/payments/status/${sid}`).then((r) => {
          if (r.data.payment_status === 'paid') { toast.success('Deposit received. Thank you!'); fetchQuotes(); }
          else toast.info('Your payment is being confirmed. This page will update shortly.');
        }).catch(() => {});
      }
    }
  }, [isAuthenticated, sessionToken]);

  const verifyMagicToken = async (token) => {
    setVerifyingToken(true);
    try {
      const response = await axios.post(`${API}/api/customer-portal/verify-token`, { token });
      const { session_token, customer: customerData } = response.data;
      
      setSessionToken(session_token);
      setCustomer(customerData);
      setIsAuthenticated(true);
      
      // Store in localStorage
      localStorage.setItem('customer_session_token', session_token);
      localStorage.setItem('customer_data', JSON.stringify(customerData));
      
      // Remove token from URL
      navigate(portalPath, { replace: true });
      toast.success('Welcome to your portal!');
    } catch (error) {
      // Link already used or expired: keep an existing session, otherwise offer a fresh link
      navigate(portalPath, { replace: true });
      if (!restoreStoredSession()) {
        setLinkProblem('This link has expired or was already used. Enter your email below and we will send you a new secure link.');
      }
    } finally {
      setVerifyingToken(false);
    }
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSendingLink(true);
    try {
      await axios.post(`${API}/api/customer-portal/magic-link`, { email, quote_id: focusQuote || undefined, invoice_id: focusInvoice || undefined });
      setLinkSent(true);
      toast.success('Check your email for the access link!');
    } catch (error) {
      toast.error('Failed to send link. Please try again.');
    } finally {
      setSendingLink(false);
    }
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/customer-portal/appointments`, { headers });
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/api/customer-portal/invoices`, { headers });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/api/customer-portal/quotes`, { headers });
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/api/customer-portal/orders`, { headers });
      setOrders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchNotifPrefs = async () => {
    try {
      const res = await axios.get(`${API}/api/customer-portal/notification-preferences`, { headers });
      setNotifPrefs(res.data);
    } catch { /* defaults are fine */ }
  };

  const saveNotifPrefs = async (updated) => {
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/api/customer-portal/notification-preferences`, updated, { headers });
      setNotifPrefs(updated);
      toast.success('Notification preferences saved');
    } catch { toast.error('Failed to save preferences'); }
    finally { setSavingPrefs(false); }
  };

  const togglePref = (key) => {
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    saveNotifPrefs(updated);
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_session_token');
    localStorage.removeItem('customer_data');
    setSessionToken(null);
    setCustomer(null);
    setIsAuthenticated(false);
    setAppointments([]);
    setInvoices([]);
  };

  const openRescheduleModal = (appointment) => {
    setRescheduleAppointment(appointment);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
    setShowReschedule(true);
  };

  const submitRescheduleRequest = async () => {
    if (!rescheduleDate) {
      toast.error('Please select a new date');
      return;
    }
    
    setSubmittingReschedule(true);
    try {
      await axios.post(`${API}/api/customer-portal/reschedule-request`, {
        appointment_id: rescheduleAppointment.id,
        requested_date: rescheduleDate,
        requested_time: rescheduleTime,
        reason: rescheduleReason
      }, { headers });
      
      toast.success('Reschedule request submitted! We\'ll notify you once it\'s reviewed.');
      setShowReschedule(false);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingReschedule(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'scheduled': { color: 'bg-blue-100 text-blue-700', icon: Calendar },
      'en_route': { color: 'bg-amber-100 text-amber-700', icon: Clock },
      'in_progress': { color: 'bg-green-100 text-green-700', icon: RefreshCw },
      'completed': { color: 'bg-green-500 text-white', icon: CheckCircle },
      'cancelled': { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig['scheduled'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  // Verifying magic token
  if (verifyingToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying your access...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl" data-testid="portal-login-brand-title">{brandName}</CardTitle>
            <CardDescription>Customer Portal</CardDescription>
          </CardHeader>
          <CardContent>
            {linkSent ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Check Your Email</h3>
                <p className="text-gray-600 mb-4">
                  We've sent a magic link to <strong>{email}</strong>
                </p>
                <p className="text-sm text-gray-500">
                  Click the link in your email to access your portal. The link is valid for 24 hours.
                </p>
                <Button 
                  variant="link" 
                  onClick={() => setLinkSent(false)}
                  className="mt-4"
                >
                  Use a different email
                </Button>
              </div>
            ) : (
              <form onSubmit={sendMagicLink} className="space-y-4">
                {(linkProblem || focusQuote) && (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="portal-link-notice">
                    {linkProblem || 'To view your quote, enter the email address it was sent to and we will email you a secure sign-in link.'}
                  </p>
                )}
                <div>
                  <Label htmlFor="email">Your Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="portal-email-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  disabled={sendingLink}
                  data-testid="send-magic-link-btn"
                >
                  {sendingLink ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Magic Link
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-gray-500">
                  No password needed! We'll email you a secure link to access your portal.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated portal
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight break-words" data-testid="portal-brand-title">{brandName}</h1>
            <p className="text-sm text-gray-500 truncate">Welcome, {customer?.name || customer?.email}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <PortalSiteLink />
            <Button variant="ghost" onClick={handleLogout} data-testid="portal-sign-out-btn" aria-label="Sign Out" className="px-3">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="w-full sm:w-auto justify-start overflow-x-auto" data-testid="portal-tabs">
            <TabsTrigger value="appointments" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Appointments
            </TabsTrigger>
            <TabsTrigger value="quotes" className="flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" />
              Quotes
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle>Your Appointments</CardTitle>
                <CardDescription>View and manage your scheduled appointments</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                  </div>
                ) : appointments.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No appointments found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {appointments.map(apt => (
                      <div key={apt.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-3">
                              <h3 className="font-semibold">{apt.title || apt.event_type}</h3>
                              {getStatusBadge(apt.status)}
                              {apt.rescheduled && (
                                <Badge variant="outline" className="text-purple-600">
                                  Rescheduled
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {apt.date}
                              </span>
                              {apt.start_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {apt.start_time}
                                </span>
                              )}
                            </div>
                            {apt.address && (
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <MapPin className="h-4 w-4" />
                                {apt.address}, {apt.city} {apt.state} {apt.zip_code}
                              </div>
                            )}
                            {apt.installer && (
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <User className="h-4 w-4" />
                                Technician: {apt.installer.name}
                              </div>
                            )}
                            {apt.customer_feedback && (
                              <div className="flex items-center gap-1 text-sm">
                                <span>Your rating: </span>
                                {[1,2,3,4,5].map(star => (
                                  <span key={star} className={star <= apt.customer_feedback.rating ? 'text-yellow-400' : 'text-gray-300'}>
                                    ★
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            {apt.tracking_token && ['en_route', 'in_progress'].includes(apt.status) && (
                              <Button 
                                size="sm" 
                                onClick={() => navigate(`/track/${apt.tracking_token}`)}
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                Track Live
                              </Button>
                            )}
                            {['scheduled'].includes(apt.status) && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openRescheduleModal(apt)}
                              >
                                Request Reschedule
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Quotes</CardTitle>
                <CardDescription>Review shade configurations and pricing</CardDescription>
              </CardHeader>
              <CardContent className="pb-20 sm:pb-6">
                {focusQuote && !quotes.some(q => q.id === focusQuote) && (
                  <p className="text-sm text-neutral-600 bg-neutral-50 border rounded-md p-3 mb-3" data-testid="portal-quote-not-found">
                    We couldn't find that quote under this email address.{quotes.length > 0 ? ' Here are all of your quotes.' : ''}
                  </p>
                )}
                {quotes.length === 0 ? (
                  <div className="text-center py-12 text-neutral-400">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No quotes yet</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="customer-quotes-list">
                    {quotes.map(q => {
                      const focused = q.id === focusQuote;
                      return (
                      <div key={q.id}
                        data-testid={focused ? 'portal-focused-quote' : `portal-quote-${q.id}`}
                        className={`border rounded-lg p-4 transition-colors ${focused ? 'border-amber-500 ring-2 ring-amber-200 bg-amber-50/40' : 'hover:bg-neutral-50'}`}>
                        {focused && <p className="text-xs font-semibold text-amber-700 mb-2">The quote from your email</p>}
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-sm">Quote #{(q.id || '').slice(0, 8).toUpperCase()}</p>
                            <p className="text-xs text-neutral-500 mt-0.5">{new Date(q.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              q.status === 'approved' ? 'bg-green-100 text-green-700' :
                              q.status === 'converted' ? 'bg-blue-100 text-blue-700' :
                              q.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>{q.status || 'pending'}</span>
                            {q.total != null && <p className="text-sm font-semibold mt-1">{formatPrice(q.total)}</p>}
                          </div>
                        </div>
                        {q.items && q.items.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            {(focused ? q.items : q.items.slice(0, 4)).map((item, i) => {
                              const cfg = item.config || {};
                              return (
                                <div key={i} className="flex items-center gap-2 text-xs text-neutral-600 bg-neutral-50 rounded px-2.5 py-1.5">
                                  <span className="font-medium">{cfg.room_label || 'Shade'}</span>
                                  {cfg.width && cfg.height && <><span className="text-neutral-300">|</span><span>{cfg.width}" x {cfg.height}"</span></>}
                                  {(cfg.fabric_color_name || item.fabric_color_name) && <>
                                    <span className="text-neutral-300">|</span>
                                    <span>{cfg.fabric_color_name || item.fabric_color_name}</span>
                                  </>}
                                </div>
                              );
                            })}
                            {!focused && q.items.length > 4 && <p className="text-xs text-neutral-400 pl-2">+{q.items.length - 4} more items</p>}
                          </div>
                        )}
                        {q.deposit && <PortalDeposit key={`${q.id}-${q.deposit_paid || 0}`} quoteId={q.id} headers={headers} invoiced={!!q.invoice_id} />}
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Orders</CardTitle>
                <CardDescription>Track orders and installation schedules</CardDescription>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-12 text-neutral-400">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No orders yet</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="customer-orders-list">
                    {orders.map(so => (
                      <div key={so.id} className="border rounded-lg p-4 hover:bg-neutral-50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-sm">Order #{so.so_number || (so.id || '').slice(0, 8).toUpperCase()}</p>
                            <p className="text-xs text-neutral-500 mt-0.5">{new Date(so.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              so.status === 'completed' || so.status === 'delivered' ? 'bg-green-100 text-green-700' :
                              so.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                              so.status === 'confirmed' ? 'bg-amber-100 text-amber-700' :
                              'bg-neutral-100 text-neutral-600'
                            }`}>{so.status || 'processing'}</span>
                            {so.total != null && <p className="text-sm font-semibold mt-1">{formatPrice(so.total)}</p>}
                          </div>
                        </div>

                        {/* Installation Date Banner */}
                        {so.installation_date && (
                          <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="install-date-banner">
                            <Wrench className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">
                                Installation: {new Date(so.installation_date + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                              </p>
                              {so.assigned_installer_name && (
                                <p className="text-xs text-amber-600">Installer: {so.assigned_installer_name}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {so.items && so.items.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            {so.items.slice(0, 3).map((item, i) => {
                              const cfg = item.config || {};
                              return (
                                <div key={i} className="flex items-center gap-2 text-xs text-neutral-600 bg-neutral-50 rounded px-2.5 py-1.5">
                                  <span className="font-medium">{cfg.room_label || 'Shade'}</span>
                                  {cfg.width && cfg.height && <><span className="text-neutral-300">|</span><span>{cfg.width}" x {cfg.height}"</span></>}
                                </div>
                              );
                            })}
                            {so.items.length > 3 && <p className="text-xs text-neutral-400 pl-2">+{so.items.length - 3} more items</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <CardTitle>Your Invoices</CardTitle>
                <CardDescription>View and pay your invoices</CardDescription>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No invoices found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invoices.map((invoice) => (
                      <PortalInvoiceCard key={invoice.id} inv={invoice} focused={invoice.id === focusInvoice} headers={headers} onPay={(id) => navigate(`/pay/invoice/${id}`)} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab - Notification Preferences */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Bell className="h-5 w-5 text-amber-600" /> Notification Preferences</CardTitle>
                <CardDescription>Choose which email notifications you'd like to receive</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="notification-prefs">
                  {[
                    { key: 'order_status', label: 'Order Status Updates', desc: 'Get notified when your order status changes (shipped, delivered, etc.)' },
                    { key: 'installation_date', label: 'Installation Date Alerts', desc: 'Get notified when an installation date is scheduled or changed' },
                    { key: 'promotions', label: 'Promotions & Offers', desc: 'Receive seasonal deals and exclusive promotions' },
                  ].map(pref => (
                    <div key={pref.key} className="flex items-center justify-between p-4 rounded-lg border hover:bg-neutral-50 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-neutral-800">{pref.label}</p>
                        <p className="text-xs text-neutral-500 mt-0.5">{pref.desc}</p>
                      </div>
                      <button
                        onClick={() => togglePref(pref.key)}
                        disabled={savingPrefs}
                        className={`relative w-11 h-6 rounded-full transition-colors ${notifPrefs[pref.key] ? 'bg-amber-500' : 'bg-neutral-200'}`}
                        data-testid={`toggle-${pref.key}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${notifPrefs[pref.key] ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reschedule Modal */}
      <Dialog open={showReschedule} onOpenChange={setShowReschedule}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Reschedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Current appointment:</p>
              <p className="font-medium">{rescheduleAppointment?.title}</p>
              <p className="text-sm">{rescheduleAppointment?.date} at {rescheduleAppointment?.start_time || 'TBD'}</p>
            </div>
            
            <div>
              <Label>Preferred New Date *</Label>
              <Input
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="reschedule-date-input"
              />
            </div>
            
            <div>
              <Label>Preferred Time (Optional)</Label>
              <Input
                type="time"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Let us know why you need to reschedule..."
                rows={3}
              />
            </div>
            
            <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>Your request will be reviewed by our team. We'll notify you once it's approved or if we need to suggest alternative times.</p>
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowReschedule(false)}>
                Cancel
              </Button>
              <Button 
                onClick={submitRescheduleRequest}
                disabled={submittingReschedule || !rescheduleDate}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="submit-reschedule-btn"
              >
                {submittingReschedule ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
